import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnInit,
    computed,
    inject,
    signal,
    viewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButtonModule } from "@angular/material/button";
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { EMPTY, Subject, catchError, switchMap } from "rxjs";
import { AlertsService, ListActiveAlertsResponse } from "./alerts.service";
import { AlertCardComponent } from "./alert-card/alert-card.component";

// The Active alerts list: the fallback when Telegram is down, so it shows every active alert a page at a time with
// the total, in the API's order (Critical first, then newest), refreshed by the admin.
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "app-alerts",
    templateUrl: "./alerts.component.html",
    styleUrl: "./alerts.component.scss",
    imports: [MatButtonModule, MatPaginatorModule, AlertCardComponent],
})
export class AlertsComponent implements OnInit {
    private readonly alertsService = inject(AlertsService);
    private readonly title = viewChild.required<ElementRef<HTMLHeadingElement>>("title");
    private readonly pages = new Subject<number>();

    readonly page = signal(1);
    readonly response = signal<ListActiveAlertsResponse | null>(null);
    readonly loadFailed = signal(false);

    readonly counts = computed(() => {
        const response = this.response();
        if (response === null) {
            return null;
        }
        return {
            unownedCritical: capped(response.unownedCritical, response.countCap),
            activeWithoutDelivery: capped(response.activeWithoutDelivery, response.countCap),
            undelivered: capped(response.undelivered, response.countCap),
        };
    });

    constructor() {
        // A later page click or refresh cancels the request before it, so the page shown is the one asked for last.
        this.pages
            .pipe(
                switchMap((page) =>
                    this.alertsService.list(page).pipe(
                        catchError(() => {
                            // The page already asked for is not the one on screen: its cards and their actions go.
                            this.response.set(null);
                            this.loadFailed.set(true);
                            return EMPTY;
                        }),
                    ),
                ),
                takeUntilDestroyed(),
            )
            .subscribe((response) => {
                // Resolving the last alert on the last page and reloading would show an empty page under a range
                // past the total; the last page that has alerts is shown instead.
                const lastPage = Math.max(1, Math.ceil(response.total / response.pageSize));
                if (response.items.length === 0 && this.page() > lastPage) {
                    this.load(lastPage);
                    return;
                }
                this.response.set(response);
            });
    }

    ngOnInit(): void {
        this.load(1);
    }

    refresh(): void {
        this.load(this.page());
    }

    pageChanged(event: PageEvent): void {
        // The paginator sits under the list, so the new page would otherwise open scrolled to its bottom, with the
        // focus on a button now off screen. Focusing the title scrolls to it and announces the page.
        this.title().nativeElement.focus();
        this.load(event.pageIndex + 1);
    }

    private load(page: number): void {
        this.page.set(page);
        this.loadFailed.set(false);
        this.pages.next(page);
    }
}

// The API stops each count at its cap, so the cap itself means "at least".
function capped(count: number, cap: number): string {
    return count >= cap ? `${cap.toLocaleString("en-US")}+` : count.toLocaleString("en-US");
}
